import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import Storage from 'storage/auth'
import { getFullName } from 'utils/formatString'

interface ReportSignatureProps {
  /**
   * Instante da assinatura. Recebido por prop para o laudo inteiro usar o mesmo
   * relógio (dados da análise, carimbo e rodapé) — o carimbo mostra segundos,
   * então dois `dayjs()` avaliados em pontos diferentes do render apareceriam
   * como horários divergentes no mesmo documento.
   */
  signedAt: Date
  /**
   * Logotipo do estabelecimento atual. Vem por prop pelo mesmo motivo do
   * `PrintableReport`: o root destacado do PDF não tem acesso ao Redux.
   */
  logoUrl?: string | null
}

/**
 * Formata o registro profissional no padrão `CRM 123456-SP`.
 *
 * O número é gravado com a sigla do estado colada no fim (ver
 * `AddProfessionals/useNewProfessionals.tsx`), então a UF é separada aqui. Se o
 * valor não terminar em duas letras — cadastros antigos, ou conselhos sem UF —
 * o número sai inteiro, sem inventar separador.
 */
const formatLicense = (
  license?: string,
  licenseNumber?: string
): string | null => {
  if (!license || license === 'NA' || !licenseNumber) return null

  const state = /[A-Za-z]{2}$/.exec(licenseNumber)
  if (!state) return `${license} ${licenseNumber}`

  return `${license} ${licenseNumber.slice(0, -2)}-${state[0].toUpperCase()}`
}

/**
 * Carimbo de assinatura eletrônica do laudo, no formato do carimbo do gov.br:
 * logotipo do estabelecimento à esquerda, separado por uma régua vertical dos dados de quem
 * assinou. Não há validador, token nem hash — é um carimbo declaratório.
 *
 * Lê o usuário do `localStorage` em vez de contexto React porque o relatório
 * também é montado num root destacado, fora do `<Provider>`, na geração do PDF
 * (`Details/index.tsx`).
 */
export const ReportSignature = ({
  signedAt,
  logoUrl
}: ReportSignatureProps) => {
  const currentUser = Storage.user.get()
  // Sem logotipo, ou se o bucket não responder com CORS, a coluna do logotipo
  // some e o carimbo fica só com os dados de quem assinou.
  const [logoFailed, setLogoFailed] = useState(false)
  const showLogo = Boolean(logoUrl) && !logoFailed

  // O guard existe porque `getFullName` faz `.trim()` direto nos dois campos:
  // uma sessão antiga gravada sem nome derrubaria o laudo inteiro.
  const fullName =
    currentUser?.first_name && currentUser?.last_name
      ? getFullName(currentUser)
      : ''
  const signerName = fullName
    ? `${currentUser?.treatment ? `${currentUser.treatment}. ` : ''}${fullName}`.toUpperCase()
    : ''

  const license = formatLicense(
    currentUser?.professional_license,
    currentUser?.professional_license_number
  )

  const signedAtLabel = dayjs(signedAt).format('DD/MM/YYYY HH:mm:ssZZ')

  return (
    <Box
      className='report-signature'
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        maxWidth: '12cm',
        margin: '0 auto',
        border: '0px solid #333',
        pageBreakInside: 'avoid'
      }}
    >
      {showLogo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1.5,
            py: 1,
            borderRight: '1px solid #ccc'
          }}
        >
          <img
            src={logoUrl ?? undefined}
            alt='Logotipo do estabelecimento'
            crossOrigin='anonymous'
            onError={() => setLogoFailed(true)}
            style={{ maxHeight: '32px', maxWidth: '3cm', width: 'auto' }}
          />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Typography sx={{ fontSize: '6.5pt', lineHeight: 1.5 }}>
          Documento assinado eletronicamente por
        </Typography>
        {signerName && (
          <Typography
            sx={{ fontSize: '8.5pt', fontWeight: 'bold', lineHeight: 1.4 }}
          >
            {signerName}
          </Typography>
        )}
        {license && (
          <Typography sx={{ fontSize: '7.5pt', lineHeight: 1.5 }}>
            {license}
          </Typography>
        )}
        <Typography sx={{ fontSize: '7pt', lineHeight: 1.5 }}>
          Data: {signedAtLabel}
        </Typography>
        <Typography sx={{ fontSize: '6.5pt', lineHeight: 1.5 }}>
          Assinatura gerada automaticamente pela plataforma sentinela da
          swissanalytica.
        </Typography>
      </Box>
    </Box>
  )
}

export default ReportSignature
